import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import MDButton from "./MDButton";
import { openAppAlert } from "./AppDialogs";
import { improveAdminTextCall } from "../redux/api/ai.api";

/*
  Small admin helper for description-like fields.

  Two actions:
    1. Improve current text — fixes grammar/clarity without adding facts.
    2. Generate description — creates a concise medical-science description
       from a topic prompt (optionally using current field text as context).

  The backend endpoint is shared with the mobile writing assistant, but the
  `context` prop lets the prompt specialise for pin/description editing.
*/
export default function AITextAssistantButton({
  value = "",
  onApply,
  context = "description",
  label = "AI",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState("");
  const [loadingMode, setLoadingMode] = useState(null);

  const currentText = String(value || "").trim();

  const applySuggestion = async ({ mode }) => {
    if (mode === "improve" && !currentText) {
      await openAppAlert({
        title: "Add text first",
        message: "Write some text, then AI can improve it.",
      });
      return;
    }
    if (mode === "generate_description" && !topic.trim()) {
      await openAppAlert({
        title: "Topic required",
        message: "Enter a medical science topic first, for example: Goblet cells in colon.",
      });
      return;
    }

    setLoadingMode(mode);
    try {
      const res = await improveAdminTextCall({
        text: currentText,
        topic: topic.trim(),
        mode,
        context,
      });
      const next = String(res?.text || "").trim();
      if (!next) {
        await openAppAlert({
          title: "No suggestion returned",
          message: res?.message || "AI did not return a usable suggestion.",
        });
        return;
      }
      onApply?.(next);
      setOpen(false);
      setTopic("");
    } catch (err) {
      await openAppAlert({
        title: "AI failed",
        message: err?.message || "Could not generate AI text right now.",
      });
    } finally {
      setLoadingMode(null);
    }
  };

  return (
    <>
      <MDButton
        size="small"
        variant="outlined"
        color="info"
        disabled={disabled || !!loadingMode}
        onClick={() => setOpen(true)}
        startIcon={<AutoFixHighRoundedIcon />}
      >
        {label}
      </MDButton>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>AI text helper</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Use AI to improve the current text, or generate a new description for a medical
              science topic.
            </Typography>

            <MDButton
              variant="contained"
              color="info"
              disabled={!currentText || !!loadingMode}
              onClick={() => applySuggestion({ mode: "improve" })}
            >
              {loadingMode === "improve" ? "Improving..." : "Improve current text"}
            </MDButton>

            <TextField
              label="Topic for new description"
              placeholder="Example: Goblet cells in colon, Giardia lamblia life cycle, Hassall corpuscles"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              multiline
              minRows={2}
              fullWidth
            />

            <MDButton
              variant="outlined"
              color="info"
              disabled={!topic.trim() || !!loadingMode}
              onClick={() => applySuggestion({ mode: "generate_description" })}
            >
              {loadingMode === "generate_description"
                ? "Generating..."
                : "Generate description from topic"}
            </MDButton>
          </Stack>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="secondary" onClick={() => setOpen(false)}>
            Close
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
